import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { registerReloginHandler } from '../utils/sessionExpiryBridge';

/**
 * Modale di re-login mostrata su 401 mentre l'utente lavora. La pagina resta
 * montata, così il lavoro non salvato non si perde, e dopo il login
 * l'interceptor ritenta la richiesta fallita.
 */
export const ReloginModal: React.FC = () => {
    const { user, login } = useAuth();
    const [open, setOpen] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [remember, setRemember] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    // "Il lavoro non viene perso" si mostra solo nelle pagine con contenuto
    // non salvato; altrove il testo è generico.
    const [hasUnsavedWork, setHasUnsavedWork] = useState(false);
    const resolverRef = useRef<((token: string | null) => void) | null>(null);

    // Rotte con lavoro non salvato che il re-login preserva.
    const WORK_PATH_PREFIXES = [
        '/report-editor',      // relazioni (info-investigativa, bonifiche, consulenza)
        '/pratiche/nuova', '/pratiche/modifica',
        '/preventivi/nuovo',
    ];

    useEffect(() => {
        registerReloginHandler(() => new Promise<string | null>(resolve => {
            resolverRef.current = resolve;
            // Pre-fill solo se è un'email in chiaro (il claim può essere cifrato).
            setEmail(user?.email && user.email.includes('@') ? user.email : '');
            setPassword('');
            setError(null);
            setSubmitting(false);
            // Valuta il contesto al momento in cui la sessione scade.
            const path = window.location.pathname;
            setHasUnsavedWork(WORK_PATH_PREFIXES.some(p => path.startsWith(p)));
            // Stesso storage (locale/sessione) dell'accesso originale.
            setRemember(localStorage.getItem('authToken') !== null || localStorage.getItem('authUser') !== null);
            setOpen(true);
        }));
        return () => registerReloginHandler(null);
    }, [user?.email]);

    const finish = (token: string | null) => {
        setOpen(false);
        setPassword('');
        const r = resolverRef.current;
        resolverRef.current = null;
        r?.(token);
    };

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);
        setError(null);
        try {
            await login(email.trim(), password, remember);
            const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
            finish(token);
        } catch (err: any) {
            setError(err?.response?.data?.message || 'Credenziali non valide. Riprova.');
            setSubmitting(false);
        }
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
            <form onSubmit={onSubmit} className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 border border-slate-100">
                <h3 className="text-lg font-bold text-slate-900 mb-1">Sessione scaduta</h3>
                <p className="text-sm text-slate-500 mb-4">
                    {hasUnsavedWork
                        ? <>Rientra per continuare. Il lavoro su questa pagina <strong>non</strong> viene perso.</>
                        : <>Accedi per continuare.</>}
                </p>
                {error && (
                    <div className="mb-3 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">{error}</div>
                )}
                <label className="block text-sm text-slate-700 mb-3">
                    <span className="block mb-1 font-medium">Email</span>
                    <input
                        type="email"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        required
                        autoFocus={!email}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-300"
                    />
                </label>
                <label className="block text-sm text-slate-700 mb-3">
                    <span className="block mb-1 font-medium">Password</span>
                    <input
                        type="password"
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        required
                        autoFocus={!!email}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-300"
                    />
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-600 mb-5 cursor-pointer">
                    <input type="checkbox" checked={remember} onChange={e => setRemember(e.target.checked)} className="w-4 h-4 rounded" />
                    Ricordami su questo dispositivo
                </label>
                <div className="flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={() => finish(null)}
                        disabled={submitting}
                        className="px-4 py-2 text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 font-medium disabled:opacity-50"
                        title="Esci e vai alla pagina di login (perdi le modifiche non salvate)"
                    >
                        Vai al login
                    </button>
                    <button
                        type="submit"
                        disabled={submitting}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-bold disabled:opacity-60"
                    >
                        {submitting ? 'Accesso…' : (hasUnsavedWork ? 'Rientra' : 'Accedi')}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default ReloginModal;
